// src/services/aeo/scanDraftTypes.ts
//
// The scan draft contract: the 50 questions are drafted and reviewed BEFORE the
// paid scan runs. One draft per site (domain + base path) is open at a time.
// Kinds: a new business (`new_scan`), editing an existing business's approved
// set (`edit_set`), and a recipe update offered to an existing business
// (`recipe_change`).
//
// Purpose (joined journey, JOURNEY-SPEC v1.1 §2.5 and §4): a `new_scan` draft
// for a signed client is its First AI check (`purpose: "first_check"`), drafted
// from the pitched 50, the client's form and the quote. The server stores the
// purpose as `first_check` or null; null reads as the kind's own purpose.
//
// Routes live under /api/v1/scan-drafts; the route list is in scanDraft.ts.
// Wire format is camelCase EXCEPT question rows, which keep the stored
// results.queries keys (`fill_vars`, `fill_values`, `original_text`, ...) so a
// started draft is copied into the assessment verbatim.
//
// Every write sends the `version` it read and gets the whole draft back. A
// stale version is refused (`stale`) and the screen reloads the latest.

export type ScanDraftKind = "new_scan" | "edit_set" | "recipe_change";

/**
 * Why a draft exists. `first_check`: a signed client's first AI check (its
 * Start writes the onboarding record's first assessment and sets the
 * baseline). The other three mirror the kind. Absent or null on the wire: the
 * kind's own purpose.
 */
export type ScanDraftPurpose = "new_scan" | "first_check" | "edit_set" | "recipe_change";

/** A draft's purpose, null and absent read as its kind. */
export function draftPurpose(d: { kind: ScanDraftKind; purpose?: ScanDraftPurpose | null }): ScanDraftPurpose {
    return d.purpose ?? d.kind;
}

/** A signed client's First AI check draft. */
export function isFirstCheckDraft(d: { kind: ScanDraftKind; purpose?: ScanDraftPurpose | null }): boolean {
    return draftPurpose(d) === "first_check";
}

export type ScanDraftStatus =
    | "building" // the words and the 50 are being generated (job running)
    | "ready" // reviewable and editable
    | "approved" // Review done; frozen until reopened or started
    | "started" // a paid scan was created from it
    | "failed" // generation failed; job.error says why
    | "discarded"
    | "superseded";

/** How customers buy. Picks the recipe. */
export type BuyMode = "visit" | "online" | "both" | "we_go_to_them";

export type RecipeShape = BuyMode;

/**
 * v4.5 (15 Oct 2026). How a business is found: by place (`local`, Local v4.5,
 * which is the v4.2 problem-led questions unchanged: Visit us, Both, We go to
 * them) or by what it sells and the problem
 * it solves (`brand`: Online, meaning no outlet a customer visits). Decided
 * before any question is written. A business with a door (a showroom plus an
 * online store) is local. Spec: aeo_visibility_spec.json `modes`.
 */
export type ScanDraftMode = "local" | "brand";

/** The mode a buy answer implies: Online is brand, everything with a door is local. */
export function scanDraftModeFor(buy: BuyMode): ScanDraftMode {
    return buy === "online" ? "brand" : "local";
}

/** A market a brand sells to. `global` = no market named in the question. */
export type ScanMarketCode = "us" | "my" | "sg" | "au" | "uk" | "global";

export interface ScanDraftMarket {
    code: ScanMarketCode;
    /** As shown and as written in a question: "the US", "Malaysia". */
    label: string;
}

/** Local mode checks (Local v4.5, unchanged from v4.2). */
export type LocalQuestionCheck = "location" | "specialty" | "intent";

/**
 * Brand mode (v4.5) checks, never shown by these names. On screen:
 * pick = "Recommend you" (28: pick_problem 20 + pick_product 8),
 * source = "Cite your site" (14, informational, won on a citation),
 * supplier = "Business buyers" (8; 0 for a pure D2C store, the 8 go to pick_problem).
 */
export type BrandQuestionCheck = "pick" | "source" | "supplier";

/** Every check a question row can carry on the wire, both modes. */
export type ScanQuestionCheck = LocalQuestionCheck | BrandQuestionCheck;

/**
 * The check on a question row, as the FE build reads it today: local only.
 * Flips to `ScanQuestionCheck` with the brand fixtures (SPEC-v45 §10): the
 * fixture engine (assemble.ts countsOf + lintRows, lint.ts LintRow,
 * render.ts CHECK_ORDER) indexes local-only maps by it, so widening it here
 * alone breaks the type check.
 */
export type QuestionCheck = LocalQuestionCheck;

/**
 * Where a business fact came from: the website read, or staff. A first_check
 * draft adds `form` ("From the client's form"), `scan` ("From the pitch": the
 * pitched scan's inputs) and `quote` ("From the quote"). Optional readers
 * treat an unknown value as `site`.
 */
export type ItemSource = "site" | "staff" | "form" | "scan" | "quote";

/** Malay / Chinese rows. Auto = decided from reviews in the draft (on from 0.3). */
export type LanguageMode = "auto" | "on" | "off";

export interface ScanDraftOutlet {
    /** The area customers type ("Bangsar"). The first outlet is the flagship. */
    name: string;
    /** The town city-level questions name for this outlet ("KL"). */
    town: string;
    source: ItemSource;
    /**
     * Staff confirmed an outlet the site does not name. Without it, an
     * unverified branch gets no questions (the backend's outlet check).
     */
    confirmed?: boolean;
    /**
     * Staff's answer when the area is also a town (city_rule
     * anchor_is_not_a_town): the business has no smaller area customers name,
     * so the town stays the anchor and `town` is the wider ring they type
     * (KL for a PJ clinic). Without it, an area equal to its town is refused.
     */
    noSmallerArea?: boolean;
    /**
     * Proposed (BE handoff, area names): other area names customers use for this same
     * place ("Janda Baik", "Bentong" for a villa in Bukit Tinggi). Location questions are
     * asked under each name and all count toward this one outlet. Absent = none.
     */
    alsoSearchedAs?: string[];
}

export interface ScanDraftService {
    /** As the site writes it; the draft turns it into the words customers type. */
    name: string;
    source: ItemSource;
}

/** The Business step, as confirmed by staff. Sent whole on create, partially on PATCH. */
export interface ScanDraftBusiness {
    businessName: string;
    category: string;
    buy: BuyMode;
    /**
     * v4.5: derived from `buy` (scanDraftModeFor), sent so the server and the
     * screen agree. Absent on drafts from before v4.5: read it as local unless
     * `buy` is "online".
     */
    mode?: ScanDraftMode;
    /**
     * Deprecated by `markets` (v4.5). Online and Both only: the country it
     * sells to ("Malaysia"). Still sent for Both and for older drafts.
     */
    market?: string | null;
    /**
     * v4.5, brand mode: where it sells and wants to sell, in priority order,
     * the home market LAST (it gets the fewest rows). At least 1. Absent on
     * local drafts.
     */
    markets?: ScanDraftMarket[];
    /** v4.5, brand mode: false = pure D2C, so 0 business-buyer rows (the 8 go to pick_problem). Absent = unknown, read as false. */
    sellsToBusinesses?: boolean;
    /** v4.5, brand mode: other brands' products that carry this one ("Sample Labs ProBlend"). Each counts as a win when named. */
    partnerProducts?: string[];
    /** Fallback town for city-level questions when an outlet has none. */
    city: string;
    /** Visit / Both: outlets. We go to them: the areas served. Online: empty. */
    outlets: ScanDraftOutlet[];
    services: ScanDraftService[];
    languages: { ms: LanguageMode; zh: LanguageMode };
    /** Change the counting, not the questions. */
    rivals: string[];
    otherNames: string[];
    /** Names that are NOT this business (a sibling brand). Round-trip it untouched. */
    notUs?: string[];
    requestedBy: string | null;
}

export type ScanDraftJobStep =
    | "reading_site"
    | "checking_outlets"
    | "researching"
    | "writing_words"
    | "filling"
    | "checking";

/**
 * v4.5 brand job step, between `researching` and `writing_words`: Google's
 * live suggestions pulled per market. On screen: "Pulling Google searches for
 * <markets>". Joins `ScanDraftJobStep` with the brand fixtures (SPEC-v45 §10):
 * DraftBuilding's step copy is keyed by the local steps only.
 */
export type BrandJobStep = "pulling_demand";

/** Every job step on the wire, both modes. */
export type AnyScanDraftJobStep = ScanDraftJobStep | BrandJobStep;

export interface ScanDraftJob {
    id: string;
    status: "queued" | "running" | "done" | "failed";
    // Both modes' steps: a brand (Online) job reports `pulling_demand` too (Jack, #4285).
    step: AnyScanDraftJobStep | null;
    startedAt: string | null;
    /** A plain sentence, safe to show. */
    error: string | null;
    /** After a rebuild: locked questions that had to be unlocked (their outlet or town went away). */
    unlocked?: number[];
}

/** Search Console demand matched to one question. Absent = unknown, never 0. */
export interface ScanDraftSearchHit {
    /** Searches a year (Search Console impressions over 12 months). */
    searches: number;
    clicks: number;
    position: number | null;
    queries: { query: string; searches: number }[];
}

/** One of the 50. Keys after `language` mirror the stored results.queries row. */
export interface ScanDraftQuestion {
    n: number;
    slot_n?: number;
    /** Recipe group: anchor, outlets, core_service, secondary_service, ... */
    group: string;
    /** Both modes' checks: an Online draft's rows carry pick/source/supplier on the wire (Jack, #4291). */
    check: ScanQuestionCheck;
    /** The outlet area (location rows) or the variable value the slot was filled from. */
    fill: string;
    town?: string | null;
    text: string;
    language: "en" | "ms" | "zh";
    /** Variables this question was built from (empty on a reworded row). */
    fill_vars: string[];
    /** variable -> value used in this question (empty on a reworded row). */
    fill_values: Record<string, string>;
    locked: boolean;
    reworded: boolean;
    /** "Try another" was taken: another template or other words, not staff's own text. Absent reads as false. */
    swapped?: boolean;
    /** The first generated wording, kept so a reworded row can go back to it. */
    original_text: string | null;
    /** edit_set / recipe_change: differs from the approved set. */
    changed: boolean;
    /** How many "try another" options exist. 0 is valid. */
    alt_count: number;
    search: ScanDraftSearchHit | null;
    /** "text" when counts were matched on a reworded row's wording. */
    search_basis?: "fill" | "text";
    /**
     * v4.5, brand rows: the market code this row is asked for (us, my, sg, au,
     * uk, global). Same value as `fill` on a brand row. Absent on local rows.
     */
    market?: string;
    /**
     * v4.5, brand rows: the Google suggestion this row was written from (or
     * is a direct rewording of). Null = no suggestion supports it (the
     * `untraced_row` note). Absent on local rows.
     */
    trace?: { suggestion: string; market: string } | null;
}

export type WordFamily =
    | "problem"
    | "service"
    | "audience"
    | "price"
    | "place"
    | "hours"
    | "offer"
    | "comparison"
    | "language"
    | "other";

export type WordOrigin =
    | "research" // reviews, forums and the site, read in the draft
    | "site"
    | "search_console"
    | "services" // the Business step's services
    | "staff"
    | "fallback"
    | "queued_fill"; // a swap queued after an earlier scan

/** One ingredient: a variable that fills one or more of the 50. */
export interface ScanDraftWord {
    variable: string;
    /** Plain words: "Problem customers type first". */
    label: string;
    value: string;
    family: WordFamily;
    /** Question numbers this word fills today. */
    usedBy: number[];
    editable: boolean;
    origin: WordOrigin;
    /** Other picks, best first. May be empty. */
    alternatives: string[];
    /** Only for the profession word: the recipe's cap. */
    capWarn: { used: number; warnAbove: number; failAbove: number } | null;
}

export interface ScanDraftCounts {
    /** Local keys always present (0 on a brand draft); brand keys only on a brand draft. */
    check: Record<LocalQuestionCheck, number> & Partial<Record<BrandQuestionCheck, number>>;
    group: Record<string, number>;
    /** One outlet (or Online): 14 / 20 / 16 instead of 26 / 14 / 10. */
    single: boolean;
    total: number;
}

export interface ScanDraftLint {
    errors: string[];
    warnings: string[];
    checkedAt: string | null;
}

/**
 * blocker: Approve is refused while open. decide: needs an answer (fix, the
 * alternative, or leave it) before Approve. suggestion: optional (Search
 * Console). info: a note, never blocks.
 */
export type DecisionSeverity = "blocker" | "decide" | "suggestion" | "info";

export type DecisionType =
    | "lint_error"
    | "lint_error_set"
    | "duplicate"
    | "missing_word"
    | "opposite_meaning"
    | "service_unused"
    | "outlet_unverified"
    | "outlet_undeclared"
    | "country_only_place"
    | "outlets_exceed_slots"
    | "set_narrower"
    | "category_cap"
    | "language_rows"
    | "towns_thin"
    | "research_unavailable"
    | "gsc_candidate"
    | "trend_break"
    | "recipe_change"
    | "scope_warning"
    /** Info: Oura reworded rows that repeated others on generation; detail lists "n. new (was: old)" per line. */
    | "auto_reworded"
    // v4.5 brand mode lint (SPEC-v45 §8). Never raised on a local draft.
    /** Blocker: a "Recommend you" problem row names the category word (problem rows ask the want, never the product). */
    | "category_in_problem_row"
    /** Blocker: a question carries the brand's own name or one of its claim phrases. */
    | "claim_in_question"
    /** Blocker: a brand row names a city, a building or "near me"; only a market word is allowed. */
    | "place_in_brand_row"
    /** Decide: the differentiator word is longer than 5 words, or reads as a claim. */
    | "differentiator_too_long"
    /** Info: rows no Google suggestion supports; `slots` lists them. */
    | "untraced_row"
    /** Info: a market's pull returned too few suggestions to write its rows from. */
    | "demand_thin"
    /** Decide: the site read contradicts the buy answer (an address customers visit on an Online pick, or none on Visit us). */
    | "mode_mismatch"
    // Joined journey, first_check drafts only (JOURNEY-SPEC §2.5, handoff H5-a). Never raised on another purpose.
    /** Decide: the client's form lists a service the 50 do not ask about ("Add it" / "Leave it out"). */
    | "form_service_new"
    /** Decide: the pitch asks about a service the form does not list ("Keep it" / "Drop it"). */
    | "form_service_missing"
    /** Decide: the form names an outlet area the 50 do not ask about ("Add {area}" / "Not an outlet"). */
    | "form_outlet_new"
    /** Decide: the pitch asked about an outlet the form does not list ("Keep it, it is open" / "Leave it out"). */
    | "form_outlet_missing"
    /** Decide: the quote sold a different number of outlets than the 50 ask about ("Add ..." / "{m} is right"). */
    | "outlets_vs_plan"
    /** Decide, "For the plan": 5 to 10 priority keywords Oura picked (`picks`). Hidden when the plan holds 5 or more. */
    | "plan_keywords"
    /** Decide, "For the plan": the outlet names per-outlet tasks need (`picks`). Only with 2 or more outlets. */
    | "plan_outlet_names"
    /** Info: how these 50 compare with the pitched 50; the Review's pitch line, never listed with the notes. */
    | "presale_set"
    /**
     * Decide: the pitch scan on these 50 is still running ("Use the pitch scan" / "Start a new one").
     * Using it, Start starts nothing new: the first AI check is the pitch's run (H4-e).
     */
    | "pitch_running"
    /**
     * Suggestion, never blocking: a form goal clearly names a service the business offers that is not
     * the core one. Two equal choices, no recommended ("Make {service} core" / "Keep {core}").
     */
    | "form_goal_core";

/**
 * What a decision option does. Server-side kinds are applied by
 * POST /scan-drafts/{id}/decisions/{decisionId}; the client-side kinds
 * (`open_editor`, `open_word`, `connect_gsc`, `choose_each`) only move the screen.
 */
export type DecisionAction =
    | { kind: "edit_word"; variable: string; value: string }
    | { kind: "swap_question"; n: number; altId: string }
    | { kind: "reword"; n: number; text: string }
    | { kind: "confirm_outlet"; name: string }
    | { kind: "drop_outlet"; name: string }
    /** `source`: where the place came from ("form" for an area the client's form names). Absent: staff. */
    | { kind: "add_outlet"; name: string; town: string; source?: ItemSource }
    /** Ask the outlet's city-level questions in a wider town ("Use KL for city-wide questions"). Draft again applies it. */
    | { kind: "set_town"; outlet: string; town: string }
    | { kind: "rebuild" }
    | { kind: "refresh_words" }
    /** v4.5 `mode_mismatch`: change how customers buy and draft again ("Use Both: the showroom gets the location questions"). */
    | { kind: "set_buy"; buy: BuyMode }
    | { kind: "recipe"; mode: "take_all" | "keep_all" }
    | { kind: "ack" }
    | { kind: "dismiss" }
    // first_check (JOURNEY-SPEC §2.5). Each changes only the rows that use that service or outlet.
    /** `form_service_new` "Add it": the service joins the business and takes a service word's rows. */
    | { kind: "add_service"; name: string }
    /** `form_service_missing` "Drop it": the service leaves the business and its rows ask the next pick. */
    | { kind: "drop_service"; name: string }
    /** Recorded, nothing changes: "Keep it", "Keep it, it is open", "{m} is right", "Use these 9". */
    | { kind: "record" }
    /** `plan_keywords` "Use these {n}" (or the edited list): written to the plan at Start. */
    | { kind: "set_plan_keywords"; keywords: string[] }
    /** `plan_outlet_names` "Use these" (or the edited list): written to the plan at Start. */
    | { kind: "set_plan_outlets"; names: string[] }
    | { kind: "open_editor"; n: number }
    | { kind: "open_word"; variable: string }
    | { kind: "connect_gsc" }
    | { kind: "choose_each" }
    /** "Add an outlet" / "Name the area": the Business step at the outlet list. `area`: the field opens with it. */
    | { kind: "open_business"; area?: string }
    /** "Edit them" on `plan_keywords`: the picks become a tag input (5 to 10). */
    | { kind: "edit_plan_keywords" }
    /** "Edit them" on `plan_outlet_names`. */
    | { kind: "edit_plan_outlets" }
    /** "Choose the 9" on a chain's cap card: the Business step's outlet list. */
    | { kind: "choose_outlets" }
    /** `pitch_running` "Use the pitch scan": Start makes the running pitch the first AI check, nothing new is spent. */
    | { kind: "use_pitch_run" }
    /** `form_goal_core` "Make {service} core": the service takes the core-service word; the word it held takes the old one. */
    | { kind: "set_core_service"; name: string };

export const CLIENT_SIDE_ACTIONS: ReadonlyArray<DecisionAction["kind"]> = [
    "open_editor",
    "open_word",
    "connect_gsc",
    "choose_each",
    "open_business",
    "edit_plan_keywords",
    "edit_plan_outlets",
    "choose_outlets",
];

export interface DecisionOption {
    /** Verb first, names the result: "Use RM120", "Reword 44". */
    label: string;
    action: DecisionAction;
}

export interface ScanDraftDecision {
    /** Deterministic, so a dismissal survives recomputation. */
    id: string;
    type: DecisionType;
    severity: DecisionSeverity;
    /** "No published price on the site". */
    title: string;
    detail: string | null;
    /** Question wordings to show as evidence, in order. */
    quotes: string[];
    /** Question numbers involved. */
    slots: number[];
    state: "open" | "resolved" | "dismissed" | "acked";
    /** The blue button. Null when the only path is manual. */
    recommended: DecisionOption | null;
    alternatives: DecisionOption[];
    /** gsc_candidate only: the search that motivated it. */
    evidence: { query: string; searches: number } | null;
    /**
     * Where the suggested values come from, in one line ("Seen on 3 nearby
     * physio sites: RM100 to RM150"). Absent or null: the values have no
     * source, so typing one is the recommended path.
     */
    source?: string | null;
    /**
     * `plan_keywords` / `plan_outlet_names`: the phrases or names Oura picked,
     * shown as chips; once answered, the ones chosen. Absent on other types.
     */
    picks?: string[];
    /**
     * First_check form cards: "From {contact}'s form, {D Mon}", the card's
     * source line. Absent on other types.
     */
    origin?: string | null;
}

/** An engine's result for one question in the business's latest scan. */
export interface ScanDraftLastResult {
    engine: "chatgpt" | "gemini" | "google_ai";
    named: boolean;
    /** Place in the answer's list; null when not named or the answer had no list. Never 0. */
    rank: number | null;
}

export interface ScanDraftRecipeRow {
    n: number;
    current: string;
    proposed: string;
    /** Identical wording: nothing to choose. */
    same: boolean;
    /** Must follow the new recipe (its group changed). */
    forced: boolean;
    pick: "take" | "keep" | null;
    /**
     * The old wording's result in the latest scan, per engine (filled from the
     * business's latest answers index). Absent or null: not measured.
     */
    last?: ScanDraftLastResult[] | null;
}

export interface ScanDraftRecipeChange {
    from: string;
    to: string;
    fromLabel: string;
    toLabel: string;
    mode: "take_all" | "keep_all" | "choose_each" | null;
    rows: ScanDraftRecipeRow[];
}

export type ScanDraftSearchStatus =
    | "connected"
    | "not_connected"
    | "action_needed"
    | "not_pulled_yet"
    | "pull_failed";

export interface ScanDraftSearchState {
    connected: boolean;
    status: ScanDraftSearchStatus;
    /** The Search Console property, e.g. "sc-domain:samplephysio.my". */
    property: string | null;
    fetchedAt: string | null;
    /** Questions with a search match. */
    matched: number;
    /** A plain sentence when connected but no counts can be shown. */
    absentReason: string | null;
    /** The year of searches is being pulled; counts arrive when it finishes. */
    pulling?: boolean;
}

/** v4.5: one Google suggestion from the demand pull, for the Searches view. */
export interface ScanDraftDemandSuggestion {
    /** As Google suggested it, lower case: "best testosterone booster for men over 40". */
    text: string;
    /** Market code the pull ran for (Google `gl`). */
    market: string;
    /** The seed typed to get it ("best testosterone booster"). */
    seed: string;
    /** How many seeds returned it; the list is ranked by this, highest first. */
    recurs: number;
    /** Questions written from it, or from a direct rewording of it. Empty = unused. */
    usedBy: number[];
}

/** v4.5: the demand pull stored with a brand draft. */
export interface ScanDraftDemand {
    /** ISO time of the pull. */
    pulledAt: string;
    /** Market codes pulled, in the business's order. */
    markets: string[];
    /** Suggestions kept after dedupe, all markets. */
    suggestions: number;
    /** Question numbers no suggestion supports (the `untraced_row` note). */
    untraced: number[];
    /** The suggestions themselves, ranked per market. Optional: a summary-only payload still renders the counts. */
    items?: ScanDraftDemandSuggestion[];
}

export interface ScanDraftSummary {
    estimatedCostUsd: number;
    estimatedMinutes: number;
    probeMode: string;
    scopeWarning: string | null;
    note: string | null;
    requestedBy: string | null;
}

/** The BD's approval of the pitched 50, carried when the first check asks them unchanged (JOURNEY-SPEC §2.5). */
export interface ScanDraftCarriedApproval {
    /** Who approved the pitch ("Dana"). */
    by: string;
    /** ISO time of that approval. */
    at: string;
    /** The pitch draft it came from; null when the pitch predates drafts. */
    fromDraftId: string | null;
}

/** The pitched scan a first_check draft starts from. */
export interface ScanDraftPitch {
    assessmentId: string;
    /** ISO time the pitch scan ran: "the pitch on {D Mon}". */
    scannedAt: string;
    /** Who pitched it ("Pitched by {BD}"). Null when unknown. */
    by: string | null;
    /** The recipe stamp the pitched 50 were written on. */
    stamp: string;
    /** The stamp is reusable, so the pitched rows were copied. False: drafted fresh on the current recipe. */
    reusable: boolean;
}

/** What a first_check draft knows about the signing (JOURNEY-SPEC §2.5). */
export interface ScanDraftFirstCheck {
    /** The platform pitch. Null: none in Oura (measured on the Mac). */
    pitch: ScanDraftPitch | null;
    /** The signed quote. Null when the record has no plan yet. */
    quote: { ref: string; outlets: number } | null;
    /** The contact who answered the form ("Sarah Lim"); cards use the first name. */
    contactName: string | null;
    /** ISO time the form was answered. */
    answeredAt: string | null;
    /** Every outlet the plan names, all of them for a chain over the 9 cap. */
    planOutlets: string[];
    /** Priority keywords the plan already holds. */
    heldKeywords: string[];
    /** The "For the plan" answers, written to the plan at Start. Null: not answered yet. */
    forPlan: { priorityKeywords: string[] | null; outletNames: string[] | null };
    /**
     * The form in the client's words, read-only context for staff (H3-b): the
     * goals, the upcoming plans and anything else they wrote. Absent: an older
     * draft, or no form.
     */
    form?: { goals: string[]; plans: string; other: string } | null;
    /**
     * Questions the client's customers ask that no row asks (H3-b). Optional:
     * never a decision, never counted, never holds Approve. Each lists the rows
     * of its recipe group it could replace, weakest first, with the wording
     * that row would get; a swap is PATCH .../questions/{n} {text}, lint-gated.
     */
    customerQuestions?: ScanDraftCustomerQuestion[];
}

export interface ScanDraftCustomerQuestion {
    /** The customer's words, as the form gave them. */
    question: string;
    /** The recipe group ("price") and that group in words. Null: no recipe shape fits; a person rewords by hand. */
    group: string | null;
    groupLabel: string | null;
    options: Array<{ n: number; current: string; text: string }>;
}

export interface ScanDraft {
    id: string;
    kind: ScanDraftKind;
    /** Absent or null: the kind's own purpose (draftPurpose). */
    purpose?: ScanDraftPurpose | null;
    /** first_check created already approved from the pitch (nothing changed). Absent or null: approved here, or not yet. */
    carriedApproval?: ScanDraftCarriedApproval | null;
    /**
     * first_check with a pitch on a reusable stamp: how many of the 50 differ
     * from the pitched 50, recomputed after each answer. Null: not like for
     * like (no platform pitch, or an older recipe). Absent on other purposes.
     */
    changedVsPitch?: number | null;
    /** first_check only: the pitch, quote, form and plan facts. */
    firstCheck?: ScanDraftFirstCheck | null;
    status: ScanDraftStatus;
    version: number;
    domain: string;
    basePath: string;
    businessId: string | null;
    sourceAssessmentId: string | null;
    recipe: {
        /**
         * The stable recipe identifier. Local v4.5 is the v4.2 problem-led local questions
         * unchanged (spec: modes.local is v4.2 unchanged), so its stamp stays
         * "visibility-set-v4.2-problem-led", matching the backend's LOCAL_STAMP; only the label
         * changed (#4317, ourteam-backend#6066).
         */
        stamp: string;
        proposedStamp: string | null;
        shape: RecipeShape;
        /** "Local v4.5", "Online v1". */
        label: string;
        /** recipe_change: the label of the recipe the draft is written to ("Local v4.5"). */
        proposedLabel?: string | null;
    };
    business: ScanDraftBusiness;
    job: ScanDraftJob | null;
    questions: ScanDraftQuestion[];
    words: ScanDraftWord[];
    counts: ScanDraftCounts;
    lint: ScanDraftLint;
    decisions: ScanDraftDecision[];
    recipeChange: ScanDraftRecipeChange | null;
    diff: { changed: number[]; total: number } | null;
    trendBreak: { willBreak: boolean; reason: string | null; slots: number[] } | null;
    search: ScanDraftSearchState;
    summary: ScanDraftSummary;
    /**
     * v4.5, brand drafts: the Google suggestions pull the 50 were written
     * from, stored with the draft (and copied to the scan on start). Absent on
     * local drafts and while the pull has not run.
     */
    demand?: ScanDraftDemand;
    /**
     * edit_set / recipe_change on a business with a baseline (JOURNEY-SPEC §2.7,
     * call 13 default): the day the baseline was measured and until when Start
     * may move it ("Fix the setup, move the baseline", first 30 days, call 12).
     * Absent or null: no baseline yet, so Start offers no choice.
     */
    baseline?: { at: string; movableUntil: string | null } | null;
    /**
     * edit_set / recipe_change: the business's next monthly check (the
     * business's `next_auto_scan_at`), when monthly checks run for it. Start
     * then offers "At the next monthly check" (free, the default) beside
     * "Rescan now" (H2-c). Absent or null: monthly checks are off.
     */
    monthly?: { nextAt: string } | null;
    /**
     * A started edit_set / recipe_change that waits for the monthly check: no
     * scan was started, and that check asks this set. Absent or null: started now.
     */
    scheduledFor?: string | null;
    /** Business inputs changed since the 50 were drafted: Draft again before Approve. */
    stale: boolean;
    undo: { available: number; lastLabel: string | null };
    /**
     * Decisions answered in this draft (a decision's option, a dismiss or an
     * ack, the recipe change), net of undo. Rewords, locks and word edits do not
     * count. Optional: absent means unknown, and the outcome line shows no count.
     */
    decisionsAnswered?: number;
    createdAt: string;
    updatedAt: string;
    approvedAt: string | null;
    startedAssessmentId: string | null;
}

/** "Try another" for one question. Deterministic, no spend. */
export interface ScanDraftAlternative {
    id: string;
    kind: "ingredient" | "template";
    text: string;
    /** One line: "Uses sciatica instead of back pain". */
    why: string;
}

/** The website read (POST /detect), with the fields the Business step adds. */
export interface ScanDraftDetected {
    businessName: string;
    category: string;
    city: string;
    outlets: { name: string; town: string }[];
    services: string[];
    /** Null unless the read found a clear signal (cart, delivery pages, addresses). */
    buyGuess: BuyMode | null;
    buyWhy: string | null;
    market: string | null;
    confidence: "high" | "low";
    /**
     * v4.5: what the read saw that decides the mode, in plain words ("a cart",
     * "no address customers visit", "a Wholesale page", "distributors in the
     * US"). Absent = the read did not report signals (older backend).
     */
    signals?: string[];
    /** v4.5, brand guess only: markets the site sells to, home market last. */
    marketsGuess?: ScanDraftMarket[];
    /** v4.5, brand guess only: a Business, Wholesale or "Enquire here" page was found. Null = no signal either way. */
    sellsToBusinessesGuess?: boolean | null;
    /** v4.5, brand guess only: partner products the site names as carrying it. */
    partnerProductsGuess?: string[];
    /** Set when the read could not use the site (blocked, unreachable). */
    reason: string | null;
    /** Pages the read looked at, for the progress list. */
    pages: string[];
}

// ─── Requests ────────────────────────────────────────────────────────────────

export type CreateScanDraftRequest =
    | { kind: "new_scan"; domain: string; inputs: ScanDraftBusiness; purpose?: undefined }
    /**
     * A signed client's First AI check (H2-a): allowed when the business has an
     * onboarding record whose first check is not complete. An open draft on the
     * same site is reused and its purpose upgraded.
     */
    | { kind: "new_scan"; purpose: "first_check"; businessId: string }
    | { kind: "edit_set" | "recipe_change"; businessId: string; purpose?: undefined };

/** `reset` puts the recipe wording back and unlocks the question (it follows the words again). */
export type QuestionPatch = { text: string } | { locked: boolean } | { reset: true };

export type DecisionChoice = "recommended" | "dismiss" | "ack" | { alternative: number };

export interface StartScanDraftRequest {
    note: string | null;
    requestedBy: string | null;
    /**
     * A draft on a business with a baseline: "keep" makes a new set and the
     * baseline stays (default); "move" makes this run the new baseline, with
     * `baselineReason` (required then). Absent: "keep".
     */
    baseline?: "keep" | "move";
    baselineReason?: string | null;
    /**
     * edit_set / recipe_change on a business whose monthly checks run:
     * "next_monthly" holds the set for that check and starts nothing (free);
     * "now" starts a paid rescan. Absent: "now" (H2-c).
     */
    applyAt?: "now" | "next_monthly";
}

export interface StartScanDraftResult {
    assessmentId: string;
    draftId: string;
    businessId: string | null;
    estimatedMinutes: number;
    reused: boolean;
    /** Applied at the next monthly check: the day it runs. No scan started, and `assessmentId` is "". */
    scheduledFor?: string | null;
    /** A First AI check that took over the pitch scan still running on the same 50: nothing new started (H4-e). */
    tookOverPitch?: boolean;
}

/**
 * A scan a draft started a short while ago, so the businesses list and the
 * business page can show it running before its business or job is listed.
 * Fixtures only: on the live backend the scan jobs carry this.
 */
export interface StartedScanDraft {
    draftId: string;
    kind: ScanDraftKind;
    purpose?: ScanDraftPurpose | null;
    domain: string;
    /** Null on a new business the scan has not created yet. */
    businessId: string | null;
    businessName: string;
    /** ISO time the scan started. */
    startedAt: string;
    estimatedMinutes: number;
}

// ─── Results ─────────────────────────────────────────────────────────────────

/** How a write changed the draft, for the one-line outcome under the decisions. */
export interface ScanDraftEffect {
    /** "Now RM120 for a real published price". */
    label: string;
    /** Question numbers whose wording changed. */
    changed: number[];
    /** Locked questions that kept their wording. */
    skipped: number[];
}

export type ScanDraftFailureKind =
    | "absent" // the route is not served here (flag off, older backend)
    | "refused" // the backend said no; `message` is its sentence
    | "lint" // the change would add a check failure; `message` names it
    | "stale" // someone else changed the draft; reload
    | "exists" // a draft or business already exists; `data` says which
    | "network";

export interface ScanDraftFailure {
    ok: false;
    kind: ScanDraftFailureKind;
    message: string;
    code?: string;
    /** For `exists`: { draftId } or { businessId, businessName }. */
    data?: { draftId?: string; businessId?: string; businessName?: string };
}

export type ScanDraftResult<T> = { ok: true; data: T } | ScanDraftFailure;

/**
 * One open draft in a list (GET /scan-drafts?status=open): enough to name it
 * and pick it up again. `businessName` is empty when the form has no name yet.
 */
export interface ScanDraftListItem {
    id: string;
    kind: ScanDraftKind;
    /** Absent or null: the kind's own purpose. The Drafts fold lists only those. */
    purpose?: ScanDraftPurpose | null;
    domain: string;
    businessName: string;
    businessId: string | null;
    status: ScanDraftStatus;
    createdAt: string;
    updatedAt: string;
}

export interface ScanDraftWrite {
    draft: ScanDraft;
    effect: ScanDraftEffect | null;
}
